import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { reviewerApi } from '../../api';
import FilterableTable from '../common/FilterableTable';
import Accordion from '../common/Accordion';
import { CaseFilters as ApiCaseFilters, ReviewData as ApiReviewData } from '../../api/reviewer';

// Interface for API case data structure
interface ApiCase {
  _id: string;
  age: number;
  sex: string;
  doctorDiagnosis: string;
  doctorNote: string;
  images: Array<{ url: string; filename: string }>;
  predictedDiagnoses?: Array<{
    _id: string;
    label: string;
    confidence: number;
    reasoning: string;
    isRecommendations?: boolean;
  }>;
  doctor?: {
    name: string;
    email: string;
  };
  reviewed: boolean;
  createdAt: string;
  updatedAt: string;
}

interface CaseFilters extends ApiCaseFilters {
  status?: 'pending' | 'processing' | 'ready' | 'failed' | 'all';
}

interface ReviewData extends Omit<ApiReviewData, 'confidence'> {
  // Remove confidence field from the form
}

// Styled Components
const Container = styled.div`
  padding: 24px;
  max-width: 100%;
  
  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const Title = styled.h1`
  margin: 0 0 24px 0;
  font-size: 24px;
  font-weight: 600;
  color: #2f3941;
  
  @media (max-width: 768px) {
    font-size: 20px;
    margin-bottom: 16px;
  }
`;

const FilterSection = styled.div`
  background: white;
  border: 1px solid #e6e9ec;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 24px;
  
  @media (max-width: 768px) {
    padding: 16px;
    margin-bottom: 16px;
  }
`;

const FilterTitle = styled.h3`
  margin: 0 0 16px 0;
  font-size: 16px;
  font-weight: 600;
  color: #2f3941;
`;

const FilterGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 16px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 12px;
  }
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const FilterLabel = styled.label`
  font-size: 14px;
  font-weight: 500;
  color: #2f3941;
`;

const FilterInput = styled.input`
  background: #ffffff;
  border: 1px solid #d8dcde;
  border-radius: 4px;
  padding: 10px 12px;
  height: 40px;
  font-family: 'SF Pro', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: #2f3941;
  letter-spacing: -0.154px;
  
  &::placeholder {
    color: #c2c8cc;
  }
  
  &:focus {
    outline: none;
    border-color: #8d4f1b;
  }
  
  @media (max-width: 480px) {
    height: 36px;
    font-size: 13px;
    padding: 8px 10px;
  }
`;

const FilterSelect = styled.select`
  background: #ffffff;
  border: 1px solid #d8dcde;
  border-radius: 4px;
  padding: 10px 12px;
  height: 40px;
  font-family: 'SF Pro', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: #2f3941;
  letter-spacing: -0.154px;
  
  &:focus {
    outline: none;
    border-color: #8d4f1b;
  }
  
  @media (max-width: 480px) {
    height: 36px;
    font-size: 13px;
    padding: 8px 10px;
  }
`;

const FilterButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const FilterButton = styled.button`
  background: #ffffff;
  border: 1px solid #8d4f1b;
  border-radius: 4px;
  padding: 10px 16px;
  height: 40px;
  cursor: pointer;
  font-family: 'SF Pro', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: #8d4f1b;
  letter-spacing: -0.154px;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(141, 79, 27, 0.08);
  }
  
  @media (max-width: 768px) {
    width: 100%;
  }
  
  @media (max-width: 480px) {
    height: 36px;
    font-size: 13px;
    padding: 8px 12px;
  }
`;

const ClearButton = styled.button`
  background: white;
  color: #68737d;
  border: 1px solid #d8dcde;
  border-radius: 4px;
  padding: 10px 16px;
  height: 40px;
  cursor: pointer;
  font-family: 'SF Pro', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  transition: all 0.2s ease;
  
  &:hover {
    background: #f8f9fa;
    border-color: #c1c9d0;
  }
  
  @media (max-width: 768px) {
    width: 100%;
  }
  
  @media (max-width: 480px) {
    height: 36px;
    font-size: 13px;
    padding: 8px 12px;
  }
`;

// Modal styled components
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
  overflow-y: auto;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 12px;
  width: 100%;
  max-width: 1200px;
  max-height: 95vh;
  overflow-y: auto;
  position: relative;
  
  @media (max-width: 768px) {
    max-width: 100%;
    height: 100vh;
    border-radius: 0;
    margin: 0;
  }
`;

const ModalHeader = styled.div`
  padding: 20px 24px;
  border-bottom: 1px solid #e6e9ec;
  background: #f8f9fa;
  position: sticky;
  top: 0;
  z-index: 10;
  border-radius: 12px 12px 0 0;
  
  @media (max-width: 768px) {
    padding: 16px 20px;
    border-radius: 0;
  }
`;

const ModalBody = styled.div`
  padding: 24px;
  
  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const MobileCloseButton = styled.button`
  position: absolute;
  top: 20px;
  right: 24px;
  background: none;
  border: none;
  font-size: 28px;
  color: #68737d;
  cursor: pointer;
  padding: 0;
  line-height: 1;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  
  &:hover {
    background: #f1f3f4;
    color: #2f3941;
  }
  
  @media (max-width: 768px) {
    top: 16px;
    right: 20px;
  }
`;

const BackToReviewLink = styled.button`
  background: none;
  border: none;
  color: #0066cc;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  padding: 0;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 4px;
  
  &:hover {
    color: #0052a3;
    text-decoration: underline;
  }
`;

const CaseDetailGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 32px;
  
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 24px;
  }
`;

const CaseDetailLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const CaseDetailRight = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const CaseImage = styled.div`
  width: 100%;
  height: 300px;
  background-color: #f8f9fa;
  background-size: cover;
  background-position: center;
  border-radius: 8px;
  border: 1px solid #e6e9ec;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #68737d;
  font-size: 14px;
  
  @media (max-width: 768px) {
    height: 250px;
  }
`;

const DetailSection = styled.div`
  background: #f8f9fa;
  padding: 20px;
  border-radius: 8px;
  border: 1px solid #e6e9ec;
`;

const DetailItem = styled.div`
  margin-bottom: 16px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const DetailLabel = styled.div`
  font-size: 12px;
  color: #68737d;
  font-weight: 500;
  margin-bottom: 4px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const DetailValue = styled.div`
  font-size: 14px;
  color: #2f3941;
  line-height: 1.4;
`;

const AISection = styled.div`
  background: #f8f9fa;
  padding: 20px;
  border-radius: 8px;
  border: 1px solid #e6e9ec;
`;

const AISectionTitle = styled.h3`
  margin: 0 0 16px 0;
  font-size: 16px;
  font-weight: 600;
  color: #2f3941;
`;

const DifferentialCard = styled.div`
  background: white;
  border: 1px solid #e6e9ec;
  border-radius: 6px;
  margin-bottom: 12px;
  overflow: hidden;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const DifferentialHeader = styled.div`
  padding: 12px 16px;
  background: #fafbfb;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #e6e9ec;
`;

const DifferentialName = styled.div`
  font-weight: 600;
  color: #2f3941;
  font-size: 14px;
`;

const DifferentialPercentage = styled.div`
  font-weight: 600;
  color: #0066cc;
  font-size: 14px;
`;

const DifferentialDetails = styled.div`
  padding: 16px;
`;

const DifferentialDescription = styled.div`
  color: #68737d;
  font-size: 14px;
  line-height: 1.5;
  margin-bottom: 12px;
`;

const RiskTag = styled.span<{ risk: 'high' | 'medium' | 'low' }>`
  display: inline-block;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  background-color: ${props => 
    props.risk === 'high' ? '#ffeaa7' : 
    props.risk === 'medium' ? '#fdcb6e' : '#55a3ff'
  };
  color: ${props => 
    props.risk === 'high' ? '#856404' : 
    props.risk === 'medium' ? '#8a4700' : '#0052a3'
  };
`;

const ReviewSection = styled.div`
  background: white;
  border: 1px solid #e6e9ec;
  border-radius: 8px;
  padding: 20px;
`;

const ReviewTitle = styled.h3`
  margin: 0 0 20px 0;
  font-size: 16px;
  font-weight: 600;
  color: #2f3941;
`;

const ReviewForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 500;
  color: #2f3941;
`;

const Input = styled.input`
  background: #ffffff;
  border: 1px solid #d8dcde;
  border-radius: 4px;
  padding: 10px 12px;
  height: 40px;
  font-family: 'SF Pro', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: #2f3941;
  letter-spacing: -0.154px;
  
  &:focus {
    outline: none;
    border-color: #8d4f1b;
  }
  
  &::placeholder {
    color: #c2c8cc;
  }
  
  @media (max-width: 480px) {
    height: 36px;
    font-size: 13px;
    padding: 8px 10px;
  }
`;

const TextArea = styled.textarea`
  background: #ffffff;
  border: 1px solid #d8dcde;
  border-radius: 4px;
  padding: 10px 12px;
  min-height: 80px;
  font-family: 'SF Pro', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: #2f3941;
  letter-spacing: -0.154px;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: #8d4f1b;
  }
  
  &::placeholder {
    color: #c2c8cc;
  }
  
  @media (max-width: 480px) {
    font-size: 13px;
    padding: 8px 10px;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 8px;
  
  @media (max-width: 768px) {
    flex-direction: column-reverse;
  }
`;

const SecondaryButton = styled.button`
  background: white;
  color: #68737d;
  border: 1px solid #d8dcde;
  border-radius: 4px;
  padding: 10px 16px;
  height: 40px;
  cursor: pointer;
  font-family: 'SF Pro', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: -0.154px;
  transition: all 0.2s ease;
  
  &:hover:not(:disabled) {
    background: #f8f9fa;
    border-color: #c1c9d0;
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  
  @media (max-width: 768px) {
    width: 100%;
  }
  
  @media (max-width: 480px) {
    height: 36px;
    font-size: 13px;
    padding: 8px 12px;
  }
`;

const PrimaryButton = styled.button`
  background: #8d4f1b;
  color: #ffffff;
  border: none;
  border-radius: 4px;
  padding: 10px 16px;
  height: 40px;
  cursor: pointer;
  font-family: 'SF Pro', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: -0.154px;
  transition: all 0.2s ease;
  
  &:hover:not(:disabled) {
    background: #703815;
  }
  
  &:active {
    transform: translateY(1px);
  }
  
  &:disabled {
    background: #d8dcde;
    cursor: not-allowed;
    opacity: 0.6;
  }
  
  @media (max-width: 768px) {
    width: 100%;
  }
  
  @media (max-width: 480px) {
    height: 36px;
    font-size: 13px;
    padding: 8px 12px;
  }
`;

// Separate ReviewModal component to fix form focus issues
interface ReviewModalProps {
  caseId: string;
  cases: ApiCase[];
  reviewData: ReviewData;
  onReviewDataChange: (data: ReviewData) => void;
  onClose: () => void;
  onSubmit: (e: React.FormEvent) => void;
  submittingReview: boolean;
}

const ReviewModal: React.FC<ReviewModalProps> = React.memo(({ 
  caseId, 
  cases, 
  reviewData, 
  onReviewDataChange, 
  onClose, 
  onSubmit, 
  submittingReview 
}) => {
  const caseData = cases.find(case_ => case_._id === caseId);
  if (!caseData) return null;

  // Format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Get the first image URL
  const imageUrl = caseData.images[0]?.url || '';

  // Separate diagnoses from recommendations
  const allDiagnoses = caseData.predictedDiagnoses || [];
  const diagnoses = allDiagnoses.filter(item => !item.isRecommendations);
  const recommendationsItem = allDiagnoses.find(item => item.isRecommendations);

  // Form change handlers
  const handleSpecialistDiagnosisChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onReviewDataChange({
      ...reviewData,
      specialistDiagnosis: e.target.value
    });
  };

  const handleNotesChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    onReviewDataChange({
      ...reviewData,
      notes: e.target.value
    });
  };

  const handleRecommendationsChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    onReviewDataChange({
      ...reviewData,
      recommendations: e.target.value
    });
  };

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <MobileCloseButton onClick={onClose}>
            ×
          </MobileCloseButton>
          <Title>Review Case {caseData._id.slice(-6)}</Title>
        </ModalHeader>
        
        <ModalBody>
          <BackToReviewLink onClick={onClose}>
            ← Back to Review Queue
          </BackToReviewLink>
          
          <CaseDetailGrid>
            <CaseDetailLeft>
              <CaseImage style={{ backgroundImage: imageUrl ? `url(${imageUrl})` : 'none' }}>
                {!imageUrl && 'No Image Available'}
              </CaseImage>
              <DetailSection>
                <DetailItem>
                  <DetailLabel>Submitted by:</DetailLabel>
                  <DetailValue>{caseData.doctor?.name || 'Unknown'}</DetailValue>
                </DetailItem>
                <DetailItem>
                  <DetailLabel>Submission Date:</DetailLabel>
                  <DetailValue>{formatDate(caseData.createdAt)}</DetailValue>
                </DetailItem>
                <DetailItem>
                  <DetailLabel>Patient Info:</DetailLabel>
                  <DetailValue>{caseData.age} years old, {caseData.sex}</DetailValue>
                </DetailItem>
                <DetailItem>
                  <DetailLabel>Doctor's Diagnosis:</DetailLabel>
                  <DetailValue>{caseData.doctorDiagnosis}</DetailValue>
                </DetailItem>
                <DetailItem>
                  <DetailLabel>Clinical Information:</DetailLabel>
                  <DetailValue>{caseData.doctorNote}</DetailValue>
                </DetailItem>
              </DetailSection>
            </CaseDetailLeft>
            
            <CaseDetailRight>
              <AISection>
                <AISectionTitle>AI Differentials:</AISectionTitle>
                {diagnoses && diagnoses.length > 0 ? (
                  <>
                    {diagnoses.map((diagnosis, index) => (
                      <DifferentialCard key={diagnosis._id}>
                        <DifferentialHeader>
                          <DifferentialName>{diagnosis.label}</DifferentialName>
                          <DifferentialPercentage>
                            {Math.round(diagnosis.confidence)}%
                          </DifferentialPercentage>
                        </DifferentialHeader>
                        <DifferentialDetails>
                          <Accordion title="View Reasoning" defaultOpen={false}>
                            <DifferentialDescription>
                              {diagnosis.reasoning}
                            </DifferentialDescription>
                          </Accordion>
                          <RiskTag risk={
                            diagnosis.confidence > 70 ? 'high' : 
                            diagnosis.confidence > 40 ? 'medium' : 'low'
                          }>
                            {diagnosis.confidence > 70 ? 'High' : 
                             diagnosis.confidence > 40 ? 'Medium' : 'Low'} Confidence
                          </RiskTag>
                        </DifferentialDetails>
                      </DifferentialCard>
                    ))}
                    
                    {recommendationsItem && (
                      <DifferentialCard>
                        <DifferentialHeader>
                          <DifferentialName>{recommendationsItem.label}</DifferentialName>
                        </DifferentialHeader>
                        <DifferentialDetails>
                          <Accordion title="View Recommendations" defaultOpen={false}>
                            <DifferentialDescription>
                              {recommendationsItem.reasoning}
                            </DifferentialDescription>
                          </Accordion>
                        </DifferentialDetails>
                      </DifferentialCard>
                    )}
                  </>
                ) : (
                  <div style={{ color: '#68737d', fontStyle: 'italic' }}>
                    No AI diagnosis available
                  </div>
                )}
              </AISection>
              
              <ReviewSection>
                <ReviewTitle>Review Case</ReviewTitle>
                <ReviewForm onSubmit={onSubmit}>
                  <FormGroup>
                    <Label>Final Diagnosis *</Label>
                    <Input
                      type="text"
                      value={reviewData.specialistDiagnosis}
                      onChange={handleSpecialistDiagnosisChange}
                      placeholder="Enter final diagnosis"
                      required
                    />
                  </FormGroup>
                  
                  <FormGroup>
                    <Label>Review Notes *</Label>
                    <TextArea
                      value={reviewData.notes || ""}
                      onChange={handleNotesChange}
                      placeholder="Enter detailed review notes and assessment"
                      required
                    />
                  </FormGroup>
                  
                  <FormGroup>
                    <Label>Recommendations</Label>
                    <TextArea
                      value={reviewData.recommendations}
                      onChange={handleRecommendationsChange}
                      placeholder="Enter treatment recommendations or next steps"
                    />
                  </FormGroup>
                  
                  <ButtonGroup>
                    <SecondaryButton type="button" onClick={onClose} disabled={submittingReview}>
                      Cancel
                    </SecondaryButton>
                    <PrimaryButton type="submit" disabled={submittingReview}>
                      {submittingReview ? 'Submitting...' : 'Submit Review'}
                    </PrimaryButton>
                  </ButtonGroup>
                </ReviewForm>
              </ReviewSection>
            </CaseDetailRight>
          </CaseDetailGrid>
        </ModalBody>
      </ModalContent>
    </ModalOverlay>
  );
});

// Main Component
const ReviewerCases: React.FC = () => {
  const [cases, setCases] = useState<ApiCase[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<CaseFilters>({
    search: '',
    status: 'pending',
    sortBy: 'createdAt',
    sortOrder: 'desc',
    limit: 20,
    page: 1
  });

  // Modal state
  const [modalCaseId, setModalCaseId] = useState<string | null>(null);
  const [reviewData, setReviewData] = useState<ReviewData>({
    specialistDiagnosis: '',
    notes: '',
    recommendations: ''
  });
  const [submittingReview, setSubmittingReview] = useState(false);

  const fetchCases = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await reviewerApi.getCasesForReview(filters);
      setCases(response.cases);
    } catch (err) {
      console.error('Error fetching cases:', err);
      setError('Failed to load cases. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCases();
  }, [filters]);

  const handleFilterChange = (key: keyof CaseFilters, value: any) => {
    setFilters(prev => ({...prev, [key]: value, page: 1}));
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      status: 'pending',
      sortBy: 'createdAt',
      sortOrder: 'desc',
      limit: 20,
      page: 1
    });
  };

  const handleOpenModal = useCallback((caseId: string) => {
    setModalCaseId(caseId);
    // Reset review form when opening modal
    setReviewData({
      specialistDiagnosis: '',
      notes: '',
      recommendations: ''
    });
  }, []);

  const handleCloseModal = useCallback(() => {
    setModalCaseId(null);
    setReviewData({
      specialistDiagnosis: '',
      notes: '',
      recommendations: ''
    });
  }, []);

  // Form handlers with useCallback to prevent re-renders
  const handleDiagnosisChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setReviewData(prev => ({...prev, specialistDiagnosis: e.target.value}));
  }, []);

  const handleNotesChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setReviewData(prev => ({...prev, notes: e.target.value}));
  }, []);

  const handleRecommendationsChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setReviewData(prev => ({...prev, recommendations: e.target.value}));
  }, []);

  const handleSubmitReview = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!modalCaseId) return;

    try {
      setSubmittingReview(true);
      await reviewerApi.reviewCase(modalCaseId, reviewData);
      
      // Update the local cases data
      setCases(prevCases => 
        prevCases.map(c => 
          c._id === modalCaseId 
            ? { ...c, reviewed: true }
            : c
        )
      );
      
      handleCloseModal();
      // Refresh the cases list
      fetchCases();
    } catch (err) {
      console.error('Error submitting review:', err);
      setError('Failed to submit review. Please try again.');
    } finally {
      setSubmittingReview(false);
    }
  };

  // Filter and sort cases locally
  const filteredAndSortedCases = cases.filter(case_ => {
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      return (
        case_.doctorDiagnosis.toLowerCase().includes(searchLower) ||
        case_.doctorNote.toLowerCase().includes(searchLower) ||
        case_.doctor?.name.toLowerCase().includes(searchLower) ||
        case_._id.toLowerCase().includes(searchLower)
      );
    }
    return true;
  }).filter(case_ => {
    if (filters.status === 'pending') return !case_.reviewed;
    if (filters.status === 'processing') return case_.reviewed === false; // Assuming processing means not reviewed
    if (filters.status === 'ready') return case_.reviewed === true; // Assuming ready means reviewed
    if (filters.status === 'failed') return false; // Handle failed cases if needed
    return true; // 'all'
  });



  // Table columns configuration
  const columns = [
    {
      key: '_id',
      title: 'Case ID',
      render: (value: string) => value.slice(-6)
    },
    {
      key: 'createdAt',
      title: 'Date',
      render: (value: string) => new Date(value).toLocaleDateString()
    },
    {
      key: 'doctor.name',
      title: 'Submitted By',
      render: (value: string, case_: ApiCase) => case_.doctor?.name || 'Unknown'
    },
    {
      key: 'doctorDiagnosis',
      title: 'Doctor\'s Diagnosis',
      render: (value: string) => value
    },
    {
      key: 'age',
      title: 'Patient',
      render: (value: number, case_: ApiCase) => `${value} years, ${case_.sex}`
    },
    {
      key: 'reviewed',
      title: 'Status',
      render: (value: boolean) => value ? 'Reviewed' : 'Pending'
    }
  ];

  return (
    <Container>
      <Title>Review Cases</Title>

      <FilterSection>
        <FilterTitle>Filter Cases</FilterTitle>
        <FilterGrid>
          <FilterGroup>
            <FilterLabel>Search</FilterLabel>
            <FilterInput
              type="text"
              placeholder="Search by case ID, diagnosis, doctor..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
            />
          </FilterGroup>
          
          <FilterGroup>
            <FilterLabel>Status</FilterLabel>
            <FilterSelect
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
            >
              <option value="pending">Pending Review</option>
            
              <option value="reviewed">Reviewed</option>
             
              <option value="all">All Cases</option>
            </FilterSelect>
          </FilterGroup>
          
          <FilterGroup>
            <FilterLabel>Sort By</FilterLabel>
            <FilterSelect
              value={filters.sortBy}
              onChange={(e) => handleFilterChange('sortBy', e.target.value)}
            >
              <option value="createdAt">Date Submitted</option>
              <option value="doctorDiagnosis">Doctor's Diagnosis</option>
              <option value="doctor">Submitted By</option>
            </FilterSelect>
          </FilterGroup>
          
          <FilterGroup>
            <FilterLabel>Sort Order</FilterLabel>
            <FilterSelect
              value={filters.sortOrder}
              onChange={(e) => handleFilterChange('sortOrder', e.target.value)}
            >
              <option value="desc">Newest First</option>
              <option value="asc">Oldest First</option>
            </FilterSelect>
          </FilterGroup>
          
          <FilterGroup>
            <FilterLabel>Results Per Page</FilterLabel>
            <FilterSelect
              value={filters.limit}
              onChange={(e) => handleFilterChange('limit', parseInt(e.target.value))}
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
            </FilterSelect>
          </FilterGroup>
        </FilterGrid>
        
        <FilterButtonGroup>
          <ClearButton onClick={clearFilters}>
            Clear Filters
          </ClearButton>
          <FilterButton onClick={fetchCases}>
            Apply Filters
          </FilterButton>
        </FilterButtonGroup>
      </FilterSection>

      {error && (
        <div style={{
          padding: '16px',
          background: '#fee',
          border: '1px solid #fcc',
          borderRadius: '4px',
          color: '#c33',
          marginBottom: '16px'
        }}>
          {error}
        </div>
      )}

      <FilterableTable
        title="Review Cases"
        data={filteredAndSortedCases}
        columns={columns}
        onRowClick={(case_: ApiCase) => handleOpenModal(case_._id)}
        loading={loading}
        searchPlaceholder="Search cases..."
        showPagination={true}
        itemsPerPage={10}
        maxRows={undefined}
        mobileCardConfig={{
          titleKey: 'doctorDiagnosis',
          dateKey: 'createdAt',
          rows: [
            { label: 'Case ID', key: '_id' },
            { label: 'Submitted By', key: 'doctor.name' },
            { label: 'Patient', key: 'age' },
            { label: 'Status', key: 'reviewed' }
          ]
        }}
      />

      {modalCaseId && (
        <ReviewModal 
          caseId={modalCaseId}
          cases={cases}
          reviewData={reviewData}
          onReviewDataChange={setReviewData}
          onClose={handleCloseModal}
          onSubmit={handleSubmitReview}
          submittingReview={submittingReview}
        />
      )}
    </Container>
  );
};

export default ReviewerCases;